import { useNavigate } from "react-router-dom";

const UPDATED = "August 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-2xl text-white leading-tight">{title}</h2>
      <div className="space-y-3 text-slate-400 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-[#1E273D]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="privacy-home">
            <img src="/openword-logo.png" alt="OpenWord AI" className="w-8 h-8" />
            <span className="text-xl text-white">OpenWord AI</span>
          </button>
          <button onClick={() => navigate("/")} className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
            Back to home
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        <header className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF]">Privacy Policy</div>
          <h1 className="text-4xl sm:text-5xl text-white leading-tight">Your study is yours.</h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            OpenWord AI exists to put you in front of the Word — not to profile you, sell you, or trade on your
            attention. This policy explains, in plain language, what we collect, why, and the choices you have.
          </p>
          <p className="text-sm text-slate-500">Last updated: {UPDATED}</p>
        </header>

        <Section title="Who we are">
          <p>
            OpenWord AI (“OpenWord AI,” “we,” “us”) is a Bible‑study tool available at openwordai.com, Powered by OpenWord AI. OpenWord AI is a study tool — it is not a church, a pastor, a counselor, or
            a replacement for the Bible itself.
          </p>
        </Section>

        <Section title="Our commitment">
          <p>
            We do not sell your personal information. We do not run advertising. We do not build advertising
            profiles about you. We collect only what we need to let you study Scripture, save your work, and keep
            your account secure.
          </p>
        </Section>

        <Section title="Information we collect">
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Account information.</strong> When you sign up with email, we store your email address, your name, and a securely hashed version of your password (we never store your actual password). If you sign in with Google, we receive your name, email address, and profile picture from Google.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Your study content.</strong> The passages and topics you choose, your reflection answers, notes, first impressions (“what you notice”), saved prayers, the words you look up, and your conversations with the virtual study companion. This is saved to your account so you can return to it.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Preferences.</strong> Your chosen translation and study mode.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Feedback.</strong> If you send feedback, we store your message and the name/email attached to your account so we can reply.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Billing information.</strong> If you start a paid plan or buy a credit top‑up, your payment is processed by Stripe. We do <strong className="text-slate-200">not</strong> receive or store your full card number. We store your subscription status, plan tier, credit balance, and the identifiers Stripe provides (such as your Stripe customer and subscription IDs) so we can manage your plan.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Basic technical data.</strong> Standard information needed to operate the service securely, such as sign‑in timestamps and limited request data used to prevent abuse (e.g., repeated failed logins).</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Product usage data.</strong> Information about how you use OpenWord AI — such as which features you open and how you move through a session — collected through our analytics tool so we can understand and improve the app.</span></li>
          </ul>
        </Section>

        <Section title="How we use your information">
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>To provide the study experience: generate studies, fetch Scripture, run the virtual study companion, and save your work.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>To keep your account and our service secure.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>To send you essential emails (welcome, password reset) and to respond to feedback you send us.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>To understand and improve how OpenWord AI serves people studying Scripture, using aggregated and individual usage data.</span></li>
          </ul>
        </Section>

        <Section title="The virtual study companion">
          <p>
            When you use the virtual study companion, ask it to explain a passage, or generate a study,
            the relevant text — the passage, your question, and the context of your session — is sent to
            OpenAI (GPT‑5.6 Luna) so a response can be generated. We send only what is needed for that
            response. We do not use your conversations to train our own models. OpenAI processes this data
            under its own terms; we do not require your content to be used for advertising.
          </p>
        </Section>

        <Section title="Facebook Page &amp; social media (Meta)">
          <p>
            OpenWord AI operates an official Facebook Page. If you are a Page administrator, you may connect
            your Facebook Page to OpenWord AI so our assistant can help you respond to comments left on your
            Page's posts. When a Page is connected, we use Meta's Graph API to:
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Read comments</strong> that people leave on the connected Page's posts (the comment text, the commenter's public name where provided, and the time it was posted), so the assistant can identify genuine questions about using or joining OpenWord AI.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Publish replies</strong> as the Page, either after the Page administrator approves a drafted reply or, if the administrator explicitly turns on auto‑reply, automatically and only within strict, published guidelines.</span></li>
          </ul>
          <p>
            We access only the Page(s) you choose to connect. We do <strong className="text-slate-200">not</strong> read your
            private Facebook messages, your personal profile, or content from Pages you do not administer, and we do
            not use this data for advertising or sell it. The Page access token you provide is stored securely and is
            used only to read and reply to comments on your connected Page. You can disconnect your Page at any time
            from the OpenWord AI admin dashboard, or by removing OpenWord AI from your Facebook Business Integrations
            (Facebook → Settings → Business Integrations), which immediately revokes our access.
          </p>
        </Section>

        <Section title="Third‑party services we rely on">
          <p>To run OpenWord AI, we use a small number of trusted service providers:</p>
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">AI generation:</strong> OpenAI (GPT‑5.6 Luna) for study, chat, explanations, and the admin content tools.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Scripture text:</strong> API.Bible and bible‑api.com to retrieve Bible passages. These requests contain the passage reference, not your personal information.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Email delivery:</strong> Resend, to send account and feedback emails.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Sign‑in with Google:</strong> Google, if you choose to sign in with your Google account.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Facebook / Meta:</strong> Meta Platforms, via the Graph API, when a Page administrator connects a Facebook Page so the assistant can read and reply to comments on that Page (see “Facebook Page &amp; social media” above).</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Payments:</strong> Stripe, to process paid subscriptions and one‑time credit top‑ups. Your card details are entered on Stripe's secure checkout and handled by Stripe directly.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Product analytics:</strong> PostHog, to help us understand how OpenWord AI is used (such as feature usage and session activity) so we can improve it. PostHog does not serve ads, and we do not use it to build advertising profiles or sell your data.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">Hosting:</strong> our cloud hosting provider, which stores the data that runs the service.</span></li>
          </ul>
          <p>These providers process data only to perform their service for us, under their own privacy terms.</p>
        </Section>

        <Section title="Cookies">
          <p>
            OpenWord AI uses cookies necessary to keep you signed in securely (session/authentication
            cookies), and our analytics tool (PostHog) uses cookies or similar technology to understand app usage.
            We do not use advertising cookies or third‑party ad‑tracking. A small amount of information may
            also be stored in your browser so previously viewed passages can be read offline.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We keep your account and study content for as long as your account is active so your history is there
            when you return. If you ask us to delete your account, we will remove your personal information and
            study content, except anything we are required to keep for legal or security reasons.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You can access and update your preferences at any time in the app.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You can request a copy of your data or ask us to delete your account and study content.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You can ask us to exclude your account from product analytics.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You can revoke Google sign‑in access at any time from your Google Account settings.</span></li>
          </ul>
          <p>To make any of these requests, contact us at the address below.</p>
        </Section>

        <Section title="Security">
          <p>
            We protect your account with hashed passwords and secure, HTTP‑only session cookies, and we take
            reasonable measures to safeguard your information. No online service can promise perfect security, but
            we work to keep your study private and your account safe.
          </p>
        </Section>

        <Section title="Children">
          <p>
            OpenWord AI is intended for a general audience and is not directed to children under 13. We do not
            knowingly collect personal information from children under 13. If you believe a child has provided us
            information, please contact us and we will remove it.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this policy as OpenWord AI grows or as our providers change. When we make material changes,
            we will update the “Last updated” date above. Please review this page from time to time.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about this policy or your data? Reach us through our{" "}
            <a href="/support" className="text-[#00E5FF] hover:underline">support form</a>.
          </p>
          <p className="text-sm text-slate-500">Powered by OpenWord AI.</p>
        </Section>
      </main>

      <footer className="border-t border-[#1E273D] bg-[#0E131F]">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-slate-500">
          <button onClick={() => navigate("/")} className="text-lg text-slate-200 hover:text-[#00E5FF]">OpenWord AI</button>
          <span className="text-xs text-slate-500">© 2026 Powered by OpenWord AI</span>
        </div>
      </footer>
    </div>
  );
}
